import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useUserReservations } from '../hooks/useUserReservations';
import type { Reservation } from '../hooks/useUserReservations';
import ReservationCard from './ReservationCard';
import ChangeTimeView from './ChangeTimeView';
import ChangeSizeView from './ChangeSizeView';
import ChangeNameView from './ChangeNameView';
import CancelReservationView from './CancelReservationView';
import ChangeDateView from './ChangeDateView';

function isUpcoming(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const parts = dateStr.split(/[-T ]/);
  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return date >= today;
}

const MyReservationsTab = () => {
  const { t } = useTranslation('profile');
  const { data: reservations, isLoading, error } = useUserReservations();
  const [changeTimeReservation, setChangeTimeReservation] = useState<Reservation | null>(null);
  const [changeSizeReservation, setChangeSizeReservation] = useState<Reservation | null>(null);
  const [changeNameReservation, setChangeNameReservation] = useState<Reservation | null>(null);
  const [cancelReservation, setCancelReservation] = useState<Reservation | null>(null);
  const [changeDateReservation, setChangeDateReservation] = useState<Reservation | null>(null);

  const handleChangeDate = (reservation: Reservation) => {
    setChangeDateReservation(reservation);
  };

  const handleChangeTime = (reservation: Reservation) => {
    setChangeTimeReservation(reservation);
  };

  const handleChangeSize = (reservation: Reservation) => {
    setChangeSizeReservation(reservation);
  };

  const handleChangeName = (reservation: Reservation) => {
    setChangeNameReservation(reservation);
  };

  const handleCancel = (reservation: Reservation) => {
    setCancelReservation(reservation);
  };

  if (changeDateReservation) {
    return (
      <ChangeDateView
        reservation={changeDateReservation}
        onClose={() => setChangeDateReservation(null)}
      />
    );
  }

  if (changeTimeReservation) {
    return (
      <ChangeTimeView
        reservation={changeTimeReservation}
        onClose={() => setChangeTimeReservation(null)}
        onSuccess={() => setChangeTimeReservation(null)}
      />
    );
  }

  if (changeSizeReservation) {
    return (
      <ChangeSizeView
        reservation={changeSizeReservation}
        onClose={() => setChangeSizeReservation(null)}
        onSuccess={() => setChangeSizeReservation(null)}
      />
    );
  }

  if (changeNameReservation) {
    return (
      <ChangeNameView
        reservation={changeNameReservation}
        onClose={() => setChangeNameReservation(null)}
        onSuccess={() => setChangeNameReservation(null)}
      />
    );
  }

  if (cancelReservation) {
    return (
      <CancelReservationView
        reservation={cancelReservation}
        onClose={() => setCancelReservation(null)}
        onSuccess={() => setCancelReservation(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="w-full flex flex-col items-center py-8">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-display mb-8">{t('MY_ACCOUNT_MY_RESERVATIONS')}</h2>
        <p className="text-red-500 text-sm">{t('PROFILE_RESERVATIONS_LOAD_ERROR')}</p>
      </div>
    );
  }

  const upcoming = reservations?.filter((r) => isUpcoming(r.resDate)) ?? [];
  const past = reservations?.filter((r) => !isUpcoming(r.resDate)) ?? [];
  const hasReservations = reservations && reservations.length > 0;

  const renderCards = (list: Reservation[]) => (
    <div className="w-full space-y-4">
      {list.map((reservation) => (
        <ReservationCard
          key={reservation.id}
          reservation={reservation}
          onChangeDate={handleChangeDate}
          onChangeTime={handleChangeTime}
          onChangeSize={handleChangeSize}
          onChangeName={handleChangeName}
          onCancel={handleCancel}
        />
      ))}
    </div>
  );

  return (
    <div className="w-full flex flex-col">
      {!hasReservations ? (
        <div className="flex flex-col items-center">
          <h2 className="text-2xl font-display mb-6">{t('MY_ACCOUNT_MY_RESERVATIONS')}</h2>
          <p className="text-foreground/60">{t('MY_ACCOUNT_NO_RESERVATIONS')}</p>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <>
              {/* Section heading — 2xl, not 3xl. The tab label is the
                  page title; "Upcoming" is a section, not a banner. */}
              <h2 className="text-2xl font-display mb-5">{t('MY_ACCOUNT_MY_RESERVATIONS')}</h2>
              {renderCards(upcoming)}
            </>
          )}
          {past.length > 0 && (
            <div className={upcoming.length > 0 ? 'mt-8' : ''}>
              <h2 className="text-2xl font-display mb-5">{t('MY_ACCOUNT_PAST_RESERVATIONS')}</h2>
              {renderCards(past)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyReservationsTab;
